import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * SecurityPulseGate — a two-step modal that (1) fires a Security Pulse
 * OTP email and (2) collects the 6-digit code before letting the caller
 * finish its action (signup / commission claim / refund submit).
 *
 * Props:
 *   open       : boolean
 *   email      : the recipient (buyer's email)
 *   purpose    : "signup" | "refund_submit" | "commission_claim"
 *   displayName: optional friendly name for the greeting
 *   onClose    : void
 *   onVerified : (code:string) => void — parent finishes the action
 *                using the returned code as ``otp_code`` in the payload
 */
export function SecurityPulseGate({ open, email, purpose, displayName,
                                       onClose, onVerified }) {
  const [stage, setStage] = useState("start");  // "start" | "code"
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [ttl, setTtl] = useState(0);

  const start = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/auth/otp/start`,
        { email, purpose, display_name: displayName || null });
      setTtl(r.data.ttl_minutes || 5);
      setStage("code");
      toast.success(`Security Pulse sent to ${email}`);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d
                 : d?.message || d?.reason || "Could not send OTP");
    } finally { setBusy(false); }
  };

  const verify = async () => {
    if ((code || "").length !== 6) {
      toast.error("Enter the 6-digit code from your inbox."); return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/auth/otp/verify`, { email, purpose, code });
      onVerified?.(code);
    } catch (err) {
      const d = err?.response?.data?.detail;
      const left = d?.attempts_left;
      toast.error(
        d?.code === "expired" ? "Code expired — request a new Pulse."
        : d?.code === "too_many_attempts" ? "Too many wrong attempts — request a new Pulse."
        : left != null ? `Invalid code · ${left} attempt${left === 1 ? "" : "s"} left.`
        : "Invalid code."
      );
    } finally { setBusy(false); }
  };

  const reset = () => { setStage("start"); setCode(""); };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] flex items-center justify-center px-4"
          data-testid="security-pulse-gate"
        >
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
          <motion.div
            initial={{ y: 10, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 10, opacity: 0 }}
            className="relative w-full max-w-[440px] bg-[#0B0B0B] border border-[#3CBFAE]/30 rounded-sm p-8 shadow-[0_20px_80px_-20px_rgba(60,191,174,0.5)]"
          >
            <button type="button" onClick={onClose}
              className="absolute top-3 right-3 p-1 text-[#8A8A82] hover:text-[#F5F5F0]"
              data-testid="security-pulse-close">
              <X size={16} />
            </button>
            <div className="flex items-center gap-3 mb-5">
              <span className="grid place-items-center w-9 h-9 rounded-full text-black"
                style={{ background: "linear-gradient(135deg,#5EE6D0,#3CBFAE)",
                            boxShadow: "0 0 24px rgba(60,191,174,0.5)" }}>
                <ShieldCheck size={16} />
              </span>
              <div>
                <div className="font-grotesk text-[9px] uppercase tracking-[0.32em] text-[#5EE6D0]">
                  · Vinci Security Pulse ·
                </div>
                <div className="font-serif text-lg text-[#F5F5F0] mt-1">
                  {purpose === "signup" ? "Verify your email"
                    : purpose === "commission_claim" ? "Acknowledge your commission"
                    : "Verify to continue"}
                </div>
              </div>
            </div>

            {stage === "start" && (
              <div data-testid="pulse-stage-start">
                <p className="font-body text-sm text-[#C9C9C2] leading-relaxed">
                  We'll send a one-time 6-digit code to
                  <b className="text-[#F5F5F0] block mt-1 truncate">{email}</b>
                </p>
                <p className="font-body text-xs text-[#8A8A82] mt-3 leading-relaxed">
                  The code expires in 5 minutes. Enter it back here to complete this action.
                </p>
                <button type="button" onClick={start} disabled={busy || !email}
                  data-testid="pulse-send"
                  className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#5EE6D0] hover:bg-[#3CBFAE] text-black font-grotesk text-xs uppercase tracking-[0.2em] py-3 disabled:opacity-50 transition-colors">
                  {busy && <Loader2 size={14} className="animate-spin" />} Send my Security Pulse
                </button>
              </div>
            )}

            {stage === "code" && (
              <div data-testid="pulse-stage-code">
                <p className="font-body text-sm text-[#C9C9C2] leading-relaxed">
                  Enter the 6-digit code from your inbox.
                </p>
                <input
                  autoFocus inputMode="numeric" maxLength={6}
                  data-testid="pulse-code-input"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  onKeyDown={(e) => e.key === "Enter" && verify()}
                  placeholder="123456"
                  className="mt-4 w-full text-center bg-[#050505] border border-[#3CBFAE]/40 rounded-sm py-4 font-mono text-2xl tracking-[0.6em] text-[#5EE6D0] outline-none focus:border-[#5EE6D0] focus:shadow-[0_0_24px_rgba(60,191,174,0.35)]"
                />
                <div className="mt-3 flex items-center justify-between font-body text-[11px] text-[#8A8A82]">
                  <span>Expires in {ttl} min · check spam if not received</span>
                  <button type="button" onClick={reset} data-testid="pulse-resend"
                    className="text-[#5EE6D0] hover:underline">Resend</button>
                </div>
                <button type="button" onClick={verify} disabled={busy || code.length !== 6}
                  data-testid="pulse-verify"
                  className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#5EE6D0] hover:bg-[#3CBFAE] text-black font-grotesk text-xs uppercase tracking-[0.2em] py-3 disabled:opacity-40 transition-colors">
                  {busy && <Loader2 size={14} className="animate-spin" />} Verify & continue
                </button>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
